/**
 * React Query hooks for /api/partner-portal/*.
 *
 * Every cache key starts with ['pp', ...] so we can invalidate the whole
 * Partner Portal cache with a single root invalidation on identity switch.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import {
  partnerPortalApi,
  type AssessmentRequestStatus,
  type AssessmentStatus,
  type ComplianceStatus,
  type Dashboard,
  type DocumentRow,
  type Earnings,
  type EngagementAssessmentRow,
  type EngagementDetail,
  type EngagementStatus,
  type EngagementSummary,
  type FeatureFlagMap,
  type FindingLikelihood,
  type FindingSeverity,
  type MessageAttachment,
  type MessageRow,
  type ObligationCatalogItem,
  type ObligationType,
  type PartnerActionPlanRow,
  type PartnerActionStatus,
  type PartnerFindingRow,
  type PartnerNotificationRow,
  type PartnerPortalProfile,
  type PolicyReaderRunResult,
  type PractitionerRow,
  type AiReviewStatus,
  type AiSuggestionRow,
  type TenantProfileResponse,
  type TenantProfileUpdateBody,
} from '../api/partnerPortal';

const PP = 'pp' as const;

// ─── Dashboard ─────────────────────────────────────────────────────────────
export function useDashboard(opts?: UseQueryOptions<Dashboard>) {
  return useQuery<Dashboard>({
    queryKey: [PP, 'dashboard'],
    queryFn: () => partnerPortalApi.getDashboard(),
    ...opts,
  });
}

// ─── Engagements ───────────────────────────────────────────────────────────
export function useEngagements(params: {
  status?: EngagementStatus;
  search?: string;
} = {}) {
  return useQuery<EngagementSummary[]>({
    queryKey: [PP, 'engagements', params],
    queryFn: () => partnerPortalApi.listEngagements(params),
  });
}

export function useEngagement(id: string | undefined) {
  return useQuery<EngagementDetail>({
    queryKey: [PP, 'engagement', id],
    queryFn: () => partnerPortalApi.getEngagement(id!),
    enabled: Boolean(id),
  });
}

export function useUpdateEngagement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof partnerPortalApi.updateEngagement>[1];
    }) => partnerPortalApi.updateEngagement(id, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [PP, 'engagement', vars.id] });
      qc.invalidateQueries({ queryKey: [PP, 'engagements'] });
      qc.invalidateQueries({ queryKey: [PP, 'dashboard'] });
    },
  });
}

export function useAddStaffing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      body,
    }: {
      engagementId: string;
      body: { practitioner_id: string; role_on_engagement: string };
    }) => partnerPortalApi.addStaffing(engagementId, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'engagement', vars.engagementId] }),
  });
}

export function useEngagementAiSettings(engagementId: string | undefined) {
  return useQuery({
    queryKey: [PP, 'engagement-ai-settings', engagementId ?? null],
    queryFn: () => partnerPortalApi.getEngagementAiSettings(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

export function useSetEngagementAiSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      flagKey,
      value,
    }: {
      engagementId: string;
      flagKey: string;
      value: unknown | null;
    }) => partnerPortalApi.setEngagementAiSetting(engagementId, flagKey, value),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({
        queryKey: [PP, 'engagement-ai-settings', vars.engagementId],
      }),
  });
}

export function useReleaseStaffing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      staffingId,
    }: {
      engagementId: string;
      staffingId: string;
    }) => partnerPortalApi.releaseStaffing(engagementId, staffingId),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'engagement', vars.engagementId] }),
  });
}

// ─── Requests ──────────────────────────────────────────────────────────────
export function useRequests(status?: AssessmentRequestStatus) {
  return useQuery({
    queryKey: [PP, 'requests', status ?? null],
    queryFn: () => partnerPortalApi.listRequests(status),
  });
}

export function useAcceptRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.acceptRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'requests'] });
      qc.invalidateQueries({ queryKey: [PP, 'engagements'] });
      qc.invalidateQueries({ queryKey: [PP, 'dashboard'] });
    },
  });
}

export function useDeclineRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      partnerPortalApi.declineRequest(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'requests'] }),
  });
}

export function useWithdrawRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.withdrawRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'requests'] }),
  });
}

// ─── Practitioners ─────────────────────────────────────────────────────────
export function usePractitioners() {
  return useQuery<PractitionerRow[]>({
    queryKey: [PP, 'practitioners'],
    queryFn: () => partnerPortalApi.listPractitioners(),
  });
}

export function useCreatePractitioner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.createPractitioner>[0]) =>
      partnerPortalApi.createPractitioner(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'practitioners'] }),
  });
}

export function useUpdatePractitioner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof partnerPortalApi.updatePractitioner>[1];
    }) => partnerPortalApi.updatePractitioner(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'practitioners'] }),
  });
}

export function useAddCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      practitionerId,
      body,
    }: {
      practitionerId: string;
      body: Parameters<typeof partnerPortalApi.addCertification>[1];
    }) => partnerPortalApi.addCertification(practitionerId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'practitioners'] });
      qc.invalidateQueries({ queryKey: [PP, 'compliance-status'] });
    },
  });
}

export function useDeletePractitioner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.deletePractitioner(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'practitioners'] }),
  });
}

export function useDeleteCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ practitionerId, certId }: { practitionerId: string; certId: string }) =>
      partnerPortalApi.deleteCertification(practitionerId, certId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'practitioners'] }),
  });
}

// ─── Messages ──────────────────────────────────────────────────────────────
export function useMessages(engagementId?: string) {
  return useQuery<MessageRow[]>({
    queryKey: [PP, 'messages', engagementId ?? null],
    queryFn: () => partnerPortalApi.listMessages(engagementId),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      engagement_id: string;
      body: string;
      attachments?: MessageAttachment[];
    }) => partnerPortalApi.sendMessage(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'messages'] }),
  });
}

export function useMarkMessageRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.markMessageRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'messages'] }),
  });
}

// ─── Documents ─────────────────────────────────────────────────────────────
export function useDocuments(engagementId?: string) {
  return useQuery<DocumentRow[]>({
    queryKey: [PP, 'documents', engagementId ?? null],
    queryFn: () => partnerPortalApi.listDocuments(engagementId),
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.createDocument>[0]) =>
      partnerPortalApi.createDocument(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'documents'] }),
  });
}

export function useSubmitDocumentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.submitDocumentReview(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'documents'] }),
  });
}

export function useApproveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.approveDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'documents'] }),
  });
}

export function useRejectDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.rejectDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.deleteDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'documents'] }),
  });
}

// ─── Earnings ──────────────────────────────────────────────────────────────
export function useEarnings(params: { period_from?: string; period_to?: string } = {}) {
  return useQuery<Earnings>({
    queryKey: [PP, 'earnings', params],
    queryFn: () => partnerPortalApi.getEarnings(params),
  });
}

// ─── Compliance status ─────────────────────────────────────────────────────
export function useComplianceStatus() {
  return useQuery<ComplianceStatus>({
    queryKey: [PP, 'compliance-status'],
    queryFn: () => partnerPortalApi.getComplianceStatus(),
  });
}

export function useUploadObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      body,
    }: {
      type: ObligationType;
      body: { document_url: string; expires_at: string };
    }) => partnerPortalApi.uploadObligation(type, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'compliance-status'] }),
  });
}

// ─── Obligations catalog (CBE / FRA / GDPR / ISO clauses) ──────────────────
export function useObligationsCatalog() {
  return useQuery<ObligationCatalogItem[]>({
    queryKey: [PP, 'obligations-catalog'],
    queryFn: () => partnerPortalApi.listObligations(),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Gap assessment: per-engagement obligation scoping ────────────────────
export function useAssessments(engagementId: string | undefined) {
  return useQuery<EngagementAssessmentRow[]>({
    queryKey: [PP, 'assessments', engagementId ?? null],
    queryFn: () => partnerPortalApi.listAssessments(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export function useScopeAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { engagement_id: string; obligation_id: string }) =>
      partnerPortalApi.scopeAssessment(body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'assessments', vars.engagement_id] }),
  });
}

export function useUpdateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      engagementId,
      body,
    }: {
      id: string;
      engagementId: string;
      body: { status?: AssessmentStatus; notes?: string | null; evidence_doc_ids?: string[] };
    }) => partnerPortalApi.updateAssessment(id, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'assessments', vars.engagementId] }),
  });
}

export function useUnscopeAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; engagementId: string }) =>
      partnerPortalApi.unscopeAssessment(id),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'assessments', vars.engagementId] }),
  });
}

// ─── Findings ─────────────────────────────────────────────────────────────
export function useFindings(engagementId: string | undefined) {
  return useQuery<PartnerFindingRow[]>({
    queryKey: [PP, 'findings', engagementId ?? null],
    queryFn: () => partnerPortalApi.listFindings(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export function useCreateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      engagement_id: string;
      obligation_id?: string | null;
      title: string;
      severity: FindingSeverity;
      likelihood?: FindingLikelihood | null;
      description?: string | null;
      recommendation?: string | null;
      evidence_doc_ids?: string[];
    }) => partnerPortalApi.createFinding(body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'findings', vars.engagement_id] }),
  });
}

export function useUpdateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      engagementId,
      body,
    }: {
      id: string;
      engagementId: string;
      body: Partial<{
        title: string;
        severity: FindingSeverity;
        likelihood: FindingLikelihood | null;
        obligation_id: string | null;
        description: string | null;
        recommendation: string | null;
        evidence_doc_ids: string[];
      }>;
    }) => partnerPortalApi.updateFinding(id, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'findings', vars.engagementId] }),
  });
}

export function useDeleteFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; engagementId: string }) =>
      partnerPortalApi.deleteFinding(id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [PP, 'findings', vars.engagementId] });
      qc.invalidateQueries({ queryKey: [PP, 'action-plans', vars.engagementId] });
    },
  });
}

// ─── Action plans ─────────────────────────────────────────────────────────
export function useActionPlans(engagementId: string | undefined) {
  return useQuery<PartnerActionPlanRow[]>({
    queryKey: [PP, 'action-plans', engagementId ?? null],
    queryFn: () => partnerPortalApi.listActionPlans(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export function useCreateActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      engagement_id: string;
      finding_id?: string | null;
      title: string;
      owner: string;
      due_date: string;
      status?: PartnerActionStatus;
      notes?: string | null;
    }) => partnerPortalApi.createActionPlan(body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'action-plans', vars.engagement_id] }),
  });
}

export function useUpdateActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      engagementId,
      body,
    }: {
      id: string;
      engagementId: string;
      body: Partial<{
        title: string;
        owner: string;
        due_date: string;
        status: PartnerActionStatus;
        notes: string | null;
        finding_id: string | null;
      }>;
    }) => partnerPortalApi.updateActionPlan(id, body),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'action-plans', vars.engagementId] }),
  });
}

export function useDeleteActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; engagementId: string }) =>
      partnerPortalApi.deleteActionPlan(id),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: [PP, 'action-plans', vars.engagementId] }),
  });
}

// ─── Notifications ────────────────────────────────────────────────────────
export function useNotifications(unreadOnly = false) {
  return useQuery<PartnerNotificationRow[]>({
    queryKey: [PP, 'notifications', unreadOnly],
    queryFn: () => partnerPortalApi.listNotifications(unreadOnly),
  });
}

export function useUnreadNotificationCount() {
  return useQuery<{ count: number }>({
    queryKey: [PP, 'notifications-unread-count'],
    queryFn: () => partnerPortalApi.getUnreadNotificationCount(),
    // Poll every minute so the sidebar badge stays roughly fresh without
    // hammering the backend.
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'notifications'] });
      qc.invalidateQueries({ queryKey: [PP, 'notifications-unread-count'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => partnerPortalApi.markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'notifications'] });
      qc.invalidateQueries({ queryKey: [PP, 'notifications-unread-count'] });
    },
  });
}

// ─── AI suggestions (Phase 2) ─────────────────────────────────────────────
export function useAiSuggestions(params: {
  engagementId?: string;
  status?: AiReviewStatus;
} = {}) {
  return useQuery<AiSuggestionRow[]>({
    queryKey: [PP, 'ai-suggestions', params],
    queryFn: () =>
      partnerPortalApi.listAiSuggestions({
        engagement_id: params.engagementId,
        status: params.status,
      }),
  });
}

export function useRunPolicyReader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      engagement_id: string;
      document_title: string;
      document_text: string;
      is_banking_content?: boolean;
    }) => partnerPortalApi.runPolicyReader(body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
      qc.invalidateQueries({ queryKey: [PP, 'engagement', vars.engagement_id] });
    },
  });
}

export function useRunAiChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      engagement_id: string;
      document_title: string;
      document_text: string;
      is_banking_content?: boolean;
    }) => partnerPortalApi.runAiChain(body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
      qc.invalidateQueries({ queryKey: [PP, 'engagement', vars.engagement_id] });
    },
  });
}

/**
 * Poll a queued AI chain job until it reaches a terminal state. Pass
 * `null` to pause polling (e.g. before the chain has been enqueued).
 * When the job completes/fails we invalidate ai-suggestions and the
 * engagement detail so the spend KPI + the queue refresh automatically.
 */
export function useAiChainJobStatus(jobId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: [PP, 'ai-chain-job', jobId],
    queryFn: async () => {
      const status = await partnerPortalApi.getAiChainJobStatus(jobId!);
      if (status.state === 'completed' || status.state === 'failed') {
        qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
        qc.invalidateQueries({ queryKey: [PP, 'engagement'] });
      }
      return status;
    },
    enabled: Boolean(jobId),
    // Poll every 2s while the job is in flight; stop once it's terminal.
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s === 'completed' || s === 'failed' ? false : 2000;
    },
    refetchOnWindowFocus: false,
  });
}

export function useAnalyzeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => partnerPortalApi.analyzeDocument(documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
    },
  });
}

export function useConflicts(engagementId: string | undefined) {
  return useQuery<AiSuggestionRow[]>({
    queryKey: [PP, 'ai-conflicts', engagementId ?? null],
    queryFn: () => partnerPortalApi.listConflicts(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export function useRankConflictSources() {
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.rankConflictSources>[0]) =>
      partnerPortalApi.rankConflictSources(body),
  });
}

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.resolveConflict>[0]) =>
      partnerPortalApi.resolveConflict(body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-conflicts', vars.engagement_id] });
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
      qc.invalidateQueries({ queryKey: [PP, 'findings', vars.engagement_id] });
      qc.invalidateQueries({ queryKey: [PP, 'messages'] });
    },
  });
}

// ── Cross-mapping admin ──
export function useObligationMappings() {
  return useQuery({
    queryKey: [PP, 'obligation-mappings'],
    queryFn: () => partnerPortalApi.listObligationMappings(),
    staleTime: 60 * 1000,
  });
}

export function useCreateObligationMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.createObligationMapping>[0]) =>
      partnerPortalApi.createObligationMapping(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'obligation-mappings'] }),
  });
}

export function useUpdateObligationMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof partnerPortalApi.updateObligationMapping>[1];
    }) => partnerPortalApi.updateObligationMapping(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'obligation-mappings'] }),
  });
}

export function useDeleteObligationMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partnerPortalApi.deleteObligationMapping(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'obligation-mappings'] }),
  });
}

export function useMappingsByObligation(obligationId: string | undefined) {
  return useQuery({
    queryKey: [PP, 'mappings-by-obligation', obligationId ?? null],
    queryFn: () => partnerPortalApi.listMappingsByObligation(obligationId!),
    enabled: Boolean(obligationId),
    staleTime: 60_000,
  });
}

export function useSuggestCrossMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.suggestCrossMappings>[0]) =>
      partnerPortalApi.suggestCrossMappings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'obligation-mappings'] }),
  });
}

export function useAcceptAiSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      overrides,
      notes,
    }: {
      id: string;
      overrides?: Record<string, unknown>;
      notes?: string;
    }) => partnerPortalApi.acceptAiSuggestion(id, { overrides, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
      qc.invalidateQueries({ queryKey: [PP, 'findings'] });
    },
  });
}

export function useModifyAiSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      overrides,
      notes,
    }: {
      id: string;
      overrides: Record<string, unknown>;
      notes?: string;
    }) => partnerPortalApi.modifyAiSuggestion(id, { overrides, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] });
      qc.invalidateQueries({ queryKey: [PP, 'findings'] });
    },
  });
}

export function useRejectAiSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      notes,
    }: {
      id: string;
      reason: import('../api/partnerPortal').RejectReason;
      notes?: string;
    }) => partnerPortalApi.rejectAiSuggestion(id, { reason, notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] }),
  });
}

export function useRequestInfoOnSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      partnerPortalApi.requestInfoOnSuggestion(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'ai-suggestions'] }),
  });
}

// ─── Tenant market profile (Phase 1 substrate) ────────────────────────────
export function useTenantProfile(options?: { enabled?: boolean }) {
  return useQuery<TenantProfileResponse>({
    queryKey: [PP, 'tenant-profile'],
    queryFn: () => partnerPortalApi.getTenantProfile(),
    // Profile changes are rare — cache forever-ish.
    staleTime: 5 * 60 * 1000,
    // Gate by caller — AutoTenantLanguage passes `enabled: isPartner` so
    // non-partner sessions don't hit the partner-only endpoint.
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateTenantProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TenantProfileUpdateBody) =>
      partnerPortalApi.updateTenantProfile(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PP, 'tenant-profile'] });
    },
  });
}

// ─── Feature flags ────────────────────────────────────────────────────────
export function useFeatureFlags() {
  return useQuery<FeatureFlagMap>({
    queryKey: [PP, 'feature-flags'],
    queryFn: () => partnerPortalApi.getFeatureFlags(),
    staleTime: 60 * 1000,
  });
}

/**
 * Convenience hook: returns the typed value of one flag plus an
 * `isLoading` so callers can wait before showing AI-gated UI.
 */
export function useFeatureFlag<T = boolean>(
  key: string,
  fallback: T,
): { value: T; isLoading: boolean } {
  const q = useFeatureFlags();
  return {
    value: (q.data?.[key] as T) ?? fallback,
    isLoading: q.isLoading,
  };
}

// ─── Profile ───────────────────────────────────────────────────────────────
export function useProfile() {
  return useQuery<PartnerPortalProfile>({
    queryKey: [PP, 'profile'],
    queryFn: () => partnerPortalApi.getProfile(),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof partnerPortalApi.updateProfile>[0]) =>
      partnerPortalApi.updateProfile(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'profile'] }),
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logo_url: string) => partnerPortalApi.uploadLogo(logo_url),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PP, 'profile'] }),
  });
}

/** Invalidate the entire Partner Portal cache. Call on identity switch. */
export function useInvalidatePartnerPortal() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [PP] });
}

// ─── Platform admin ────────────────────────────────────────────────────────
export function usePilotCohort() {
  return useQuery({
    queryKey: [PP, 'admin', 'pilot-cohort'],
    queryFn: () => partnerPortalApi.listPilotCohort(),
  });
}

export function useSetPilotCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, enabled }: { tenantId: string; enabled: boolean }) =>
      partnerPortalApi.setPilotCohort(tenantId, enabled),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [PP, 'admin', 'pilot-cohort'] }),
  });
}

export function useAiMetrics() {
  return useQuery({
    queryKey: [PP, 'admin', 'ai-metrics'],
    queryFn: () => partnerPortalApi.getAiMetrics(),
  });
}
